// src/pages/channel-order/ChannelOrdersPage.jsx
// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import ReactDOM from "react-dom"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import { formatISO, parseISO } from "date-fns"
import { toDate } from "date-fns-tz"
import CustomDateRangePicker from "../../utils/CustomDateRangePicker"
import EditOrderModal from "./EditOrderModal"
import { useToast } from "../../components/toast/ToastProvider"
import { API } from "../../utils/apiUrls"
import CancelOrderDialog from "./CancelOrderDialog"
import BulkUploadModal from "./BulkUploadModal"
import { v4 as uuidv4 } from "uuid"    // ← for correlation_id, same as ShipmentNoteDetails

const lottieSrc = "/animations/loader.lottie"

// ───────────────────────────────────────────────────────────────────────────────
// Lightweight icons (no extra deps)
// ───────────────────────────────────────────────────────────────────────────────
const Icon = {
  Refresh: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 3v6h-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Download: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 10l5 5 5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 15V3" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Search: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="11" cy="11" r="8" strokeWidth="2" />
      <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Left: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M15 18l-6-6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Right: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M9 18l6-6-6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  X: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Plus: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path d="M12 5v14M5 12h14" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Edit: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path
        d="M12 20h9"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.5 3.5l4 4L7 21H3v-4L16.5 3.5z"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  EditPencil: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <path
        d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Info: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...p}>
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="8" r="0.5" fill="currentColor" strokeWidth="1" />
    </svg>
  ),
}

// ───────────────────────────────────────────────────────────────────────────────
// Visual tokens
// ───────────────────────────────────────────────────────────────────────────────
const chip = {
  base: "inline-block px-2 py-0.5 rounded-full text-[11px] font-medium",
  primary: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100",
  info: "bg-sky-50 text-sky-700 ring-1 ring-sky-100",
  success: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
  warn: "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
  danger: "bg-rose-50 text-rose-700 ring-1 ring-rose-100",
  muted: "bg-slate-50 text-slate-600 ring-1 ring-slate-100",
}

const btnBase =
  "inline-flex items-center justify-center rounded-xl transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
const actionIconBtn =
  "inline-flex items-center justify-center h-8 w-8 rounded-lg ring-1 ring-slate-300 bg-white hover:bg-slate-100 active:bg-slate-200 transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";
const actionIconDanger =
  "inline-flex items-center justify-center h-8 w-8 rounded-lg bg-rose-600 text-white hover:bg-rose-500 active:bg-rose-700 transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

// Enhanced Edit Button with premium styling
const actionIconEdit =
  "group relative inline-flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-300 " +
  "bg-gradient-to-br from-indigo-50 to-violet-50 ring-1 ring-indigo-200/60 " +
  "hover:from-indigo-100 hover:to-violet-100 hover:ring-indigo-300 hover:shadow-md hover:scale-105 " +
  "active:scale-95 active:shadow-sm " +
  "disabled:from-slate-50 disabled:to-slate-50 disabled:ring-slate-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2";

const btn = {
  primary:
    `${btnBase} gap-2 px-4 py-2 text-white bg-gradient-to-tr from-indigo-600 to-violet-600 ` +
    "hover:from-indigo-500 hover:to-violet-500 active:from-indigo-700 active:to-violet-700 focus-visible:ring-indigo-400",
  secondary:
    `${btnBase} gap-2 px-4 py-2 text-slate-700 bg-white ring-1 ring-slate-200 hover:bg-slate-50 active:bg-slate-100 focus-visible:ring-indigo-400`,
  success:
    `${btnBase} gap-2 px-4 py-2 text-white bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 focus-visible:ring-emerald-400`,
  info:
    `${btnBase} gap-2 px-4 py-2 text-white bg-sky-600 hover:bg-sky-500 active:bg-sky-700 focus-visible:ring-sky-400`,
  danger:
    `${btnBase} gap-2 px-4 py-2 text-white bg-rose-600 hover:bg-rose-500 active:bg-rose-700 focus-visible:ring-rose-400`,
  subtle:
    `${btnBase} gap-2 px-3 py-1.5 text-slate-700 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 focus-visible:ring-indigo-400`,
  ghost:
    `${btnBase} gap-2 px-3 py-1.5 text-slate-600 bg-transparent hover:bg-slate-100 active:bg-slate-200 focus-visible:ring-indigo-300`,
}

const inputCls =
  "w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 outline-none bg-white placeholder:text-slate-400"

const panel =
  "bg-white/95 supports-[backdrop-filter]:backdrop-blur rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50"

// ───────────────────────────────────────────────────────────────────────────────
// Data helpers
// ───────────────────────────────────────────────────────────────────────────────
const getOrderedPkgs = (o) => {
  const shipmentDetails = o?.shipment_details || o?.market_place_shipment_details
  return Array.isArray(shipmentDetails?.shipment_packages)
    ? shipmentDetails.shipment_packages
    : []
}

const getFulfilledPkgs = (o) => {
  const shipmentDetails = o?.shipment_details || o?.market_place_shipment_details
  return Array.isArray(shipmentDetails?.shipment_fulfilled_packages)
    ? shipmentDetails.shipment_fulfilled_packages
    : []
}

const flattenItems = (pkgs) =>
  (pkgs || []).flatMap((pkg, pkgIdx) =>
    (pkg.items || []).map((it) => ({
      ...it,
      _awb: pkg?.waybill || null,
      _pkgIdx: pkgIdx + 1,
    }))
  )

const sumQty = (items) =>
  (items || []).reduce((s, it) => s + Number(it?.quantity || 0), 0)

const sumValue = (items) =>
  (items || []).reduce((s, it) => {
    const unitRaw = it?.item_payment_details?.unit_price
    const unit =
      typeof unitRaw === "string" ? parseFloat(unitRaw) : Number(unitRaw || 0)
    const qty = Number(it?.quantity || 0)
    return s + (isNaN(unit) ? 0 : unit) * qty
  }, 0)

const deadKgOf = (pkgs) =>
  (pkgs || []).reduce((s, p) => s + Number(p?.weight?.product_weight || 0), 0) /
  1000

const volKgOf = (pkgs) =>
  (pkgs || []).reduce((s, p) => {
    const len = Number(p?.dimensions?.product_length || 0)
    const wid = Number(p?.dimensions?.product_width || 0)
    const hei = Number(p?.dimensions?.product_height || 0)
    return s + (len * wid * hei) / 5000
  }, 0)

const formatINR = (v) => (v > 0 ? `₹${v.toFixed(2)}` : "—")

// Async utils
const sleep = (ms) => new Promise((res) => setTimeout(res, ms))
const backoff = (attempt, base = 400) => Math.min(8000, base * 2 ** (attempt - 1))
const isRetryable = (err) => {
  const status = err?.response?.status
  return !status || status >= 500 || status === 429 || status === 408
}

const SPECIFIC_FIELDS = [
  "order_number",
]

async function runPool(tasks, concurrency, onProgress) {
  let i = 0, done = 0
  const results = new Array(tasks.length)
  const workers = new Array(Math.min(concurrency, tasks.length))
    .fill(0)
    .map(async () => {
      while (i < tasks.length) {
        const cur = i++
        try {
          results[cur] = await tasks[cur]()
        } catch (e) {
          results[cur] = { error: e }
        } finally {
          done++
          onProgress?.(done, tasks.length)
        }
      }
    })
  await Promise.all(workers)
  return results
}

const buildPickupDetails = (p = {}) => ({
  pickup_poc: p.pickup_poc || "",
  pickup_organisation: p.pickup_organisation || "",
  pickup_address: p.pickup_address || "",
  pickup_district: p.pickup_district || "",
  pickup_city: p.pickup_city || "",
  pickup_state: p.pickup_state || "",
  pickup_landmark: p.pickup_landmark || "",
  pickup_poc_email: p.pickup_poc_email || "",
  pickup_poc_contact_number: p.pickup_poc_contact_number || "",
  pickup_postal_code: p.pickup_postal_code || "",
  pickup_country_code: p.pickup_country_code || "",
  pickup_address_type: p.pickup_address_type || "",
  user_defined_additional_data: Array.isArray(p.user_defined_additional_data)
    ? p.user_defined_additional_data
    : [],
})

// Multi-select dropdown component with checkboxes and Select All
const MultiSelectDropdown = ({ label, options, selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 })
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)
  const tooltipRef = useRef(null)
  const menuRef = useRef(null)

  const allSelected = selected.length === options.length
  const someSelected = selected.length > 0 && selected.length < options.length

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option))
    } else {
      onChange([...selected, option])
    }
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      onChange([])
    } else {
      onChange([...options])
    }
  }

  // Calculate dropdown position (for fixed positioning, use viewport coordinates)
  const computePosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPosition({
      top: rect.bottom + 4, // Fixed positioning is relative to viewport, no scroll offset needed
      left: rect.left,
      width: rect.width
    })
  }, [])

  // Update position when opened or on scroll/resize
  useEffect(() => {
    if (!isOpen) return
    
    // Calculate position immediately and after a frame to ensure DOM is ready
    requestAnimationFrame(() => {
      computePosition()
    })
    
    const handleWindowChange = () => {
      requestAnimationFrame(() => {
        computePosition()
      })
    }
    
    window.addEventListener("resize", handleWindowChange)
    window.addEventListener("scroll", handleWindowChange, true)
    return () => {
      window.removeEventListener("resize", handleWindowChange)
      window.removeEventListener("scroll", handleWindowChange, true)
    }
  }, [isOpen, computePosition])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      // Use setTimeout to avoid immediate closure
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside)
      }, 0)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  // Get display text for button
  const getButtonText = () => {
    if (selected.length === 0) return "Select options..."
    if (allSelected) return "All selected"
    if (selected.length <= 2) {
      return selected.map(s => s.replace(/_/g, " ")).join(", ")
    }
    return `${selected.length} selected`
  }

  return (
    <div className="w-full sm:w-48 lg:w-52 relative" ref={dropdownRef} style={{ zIndex: isOpen ? 99999 : 'auto', position: 'relative' }}>
      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
        {label}
        {selected.length > 0 && (
          <span className="ml-1 text-indigo-600 font-normal">({selected.length})</span>
        )}
      </label>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          onMouseEnter={() => selected.length > 0 && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className={`${inputCls} h-[42px] text-sm text-left pr-10 hover:ring-slate-300 transition-all duration-200 cursor-pointer font-medium flex items-center justify-between w-full relative group`}
        >
          <span className="truncate flex-1 text-left">
            {getButtonText()}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            {selected.length > 0 && (
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
                {selected.length}
              </span>
            )}
            <svg
              className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {/* Tooltip showing all selected items */}
        {showTooltip && selected.length > 0 && (
          <div
            ref={tooltipRef}
            className="absolute z-[10002] bottom-full left-0 mb-2 w-full max-w-[300px] bg-slate-900 text-white text-xs rounded-lg shadow-2xl p-3 pointer-events-none"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div className="font-semibold mb-2 text-indigo-300">Selected ({selected.length}):</div>
            <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto">
              {selected.map((status) => (
                <span
                  key={status}
                  className="inline-flex items-center px-2 py-1 rounded bg-indigo-500/20 text-indigo-100 border border-indigo-400/30"
                >
                  {status.replace(/_/g, " ")}
                </span>
              ))}
            </div>
            <div className="absolute bottom-0 left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
          </div>
        )}

        {/* Dropdown menu rendered via Portal to appear above everything */}
        {isOpen && position.width > 0 &&
          ReactDOM.createPortal(
            <div
              ref={menuRef}
              className="fixed bg-white rounded-xl shadow-2xl border border-slate-200 max-h-[300px] overflow-hidden"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${position.width}px`,
                minWidth: '200px',
                zIndex: 99999,
                position: 'fixed'
              }}
            >
              <div className="p-2 border-b border-slate-200 bg-slate-50">
                <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected
                    }}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-indigo-600 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-sm font-semibold text-slate-700">
                    {allSelected ? "Deselect All" : "Select All"}
                  </span>
                </label>
              </div>
              <div className="overflow-y-auto max-h-[220px] p-1">
                {options.map((option) => {
                  const isSelected = selected.includes(option)
                  return (
                    <label
                      key={option}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-indigo-50 cursor-pointer transition-colors ${
                        isSelected ? "bg-indigo-50" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOption(option)}
                        className="h-4 w-4 accent-indigo-600 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-sm text-slate-700 flex-1 text-left">{option.replace(/_/g, " ")}</span>
                    </label>
                  )
                })}
              </div>
              {selected.length > 0 && (
                <div className="p-2 border-t border-slate-200 bg-slate-50">
                  <button
                    type="button"
                    onClick={() => {
                      onChange([])
                      setIsOpen(false)
                    }}
                    className="w-full text-xs text-rose-600 hover:text-rose-700 font-medium py-1.5 hover:bg-rose-50 rounded-lg transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
    </div>
  )
}

export default function ChannelOrdersPage() {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [useDateFilter, setUseDateFilter] = useState(true)

  const [pickupList, setPickupList] = useState([])
  const [loadingPickups, setLoadingPickups] = useState(false)

  const [creatingIds, setCreatingIds] = useState([])
  const [cancellingIds, setCancellingIds] = useState([])
  const [cancelDialog, setCancelDialog] = useState({ open: false, order: null })
  const [selectedRows, setSelectedRows] = useState(new Set())
  const headerCheckboxRef = useRef(null)
  const [loadingDownloadSelected, setLoadingDownloadSelected] = useState(false)
  const [downloadingCSV, setDownloadingCSV] = useState(false)
  const [showCSVColumnModal, setShowCSVColumnModal] = useState(false)
  
  // CSV column configuration - all available columns
  const allCSVColumns = [
    { key: "order_number", label: "Order Number" },
    { key: "channel_order_id", label: "channel_order_id", defaultSelected: true },
    { key: "order_status", label: "Order Status" },
    { key: "confirmation_status", label: "Confirmation Status" },
    { key: "store_id", label: "Store ID" },
    { key: "marketplace_channel", label: "Marketplace Channel" },
    { key: "shipment_type", label: "Shipment Type" },
    { key: "created_date_time", label: "Created Date Time" },
    { key: "updated_date_time", label: "Updated Date Time" },
    { key: "pickup_organisation", label: "Pickup Organisation" },
    { key: "pickup_address", label: "Pickup Address" },
    { key: "pickup_city", label: "Pickup City" },
    { key: "pickup_state", label: "Pickup State" },
    { key: "pickup_postal_code", label: "Pickup Postal Code" },
    { key: "drop_poc", label: "Drop POC" },
    { key: "drop_address", label: "Drop Address" },
    { key: "drop_city", label: "Drop City" },
    { key: "drop_state", label: "Drop State" },
    { key: "drop_postal_code", label: "Drop Postal Code" },
    { key: "order_type", label: "Order Type" },
    { key: "cod_amount", label: "COD Amount" },
    { key: "invoice_value", label: "Invoice Value" },
    { key: "invoice_currency", label: "Invoice Currency" },
    { key: "total_packages", label: "Total Packages" },
    { key: "fulfilled_packages", label: "Fulfilled Packages" }
  ]
  
  // State for selected CSV columns (default: only marketplace_channel_order_number)
  const [selectedCSVColumns, setSelectedCSVColumns] = useState(
    new Set(allCSVColumns.filter(col => col.defaultSelected).map(col => col.key))
  )
  const [modal, setModal] = useState({ open: false, title: "", message: "", variant: "error" })
  const openModal = (title, message, variant = "error") => setModal({ open: true, title, message, variant })
  const closeModal = () => setModal((m) => ({ ...m, open: false }))

  // Address popup state
  const [showPickupAddress, setShowPickupAddress] = useState({})
  const [showDropAddress, setShowDropAddress] = useState({})

  // Bulk tile state
  const [selectedPickupId, setSelectedPickupId] = useState("")
  const [applyingPickup, setApplyingPickup] = useState(false)
  const [applyProgress, setApplyProgress] = useState({ done: 0, total: 0 })
  const abortRef = useRef(null)

  // UI polish state
  const [showFilters, setShowFilters] = useState(true)
  const [density, setDensity] = useState("comfortable") // "comfortable" | "compact"

  // Default 30-day window
  const now = new Date()
  const thirtyDaysAgo = new Date(now)
  thirtyDaysAgo.setDate(now.getDate() - 30)

  const [inputFilters, setInputFilters] = useState({
    from_date: thirtyDaysAgo.toLocaleDateString("en-CA"),
    to_date: now.toLocaleDateString("en-CA"),
    order_number: "",
    marketplace_channel_order_number: "",
    order_statuses: [],
    confirmation_statuses: [],
  })
  const [activeFilters, setActiveFilters] = useState(inputFilters)

  // Pagination/sort
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [sort_field, setSortField] = useState("createdDateTime")
  const [sort_direction, setSortDir] = useState("DESC")

  // Data/UI
  const [shipments, setShipments] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState({})

  // ─── Manual Courier state & helpers (same behavior as ShipmentNoteDetails) ──
  const [carrierOptionsByKey, setCarrierOptionsByKey] = useState({}) // { rowKey: [{id,name,price}] }
  const [loadingCarrierKeys, setLoadingCarrierKeys] = useState(new Set())
  const [carrierSelectionByKey, setCarrierSelectionByKey] = useState({}) // { rowKey: carrier_partner_id }

  // Quick stats (memoized)
  const stats = useMemo(() => {
    const totals = shipments.reduce(
      (acc, o) => {
        const orderedItems = flattenItems(getOrderedPkgs(o))
        const fulfilledItems = flattenItems(getFulfilledPkgs(o))
        acc.orders += 1
        acc.items += sumQty(orderedItems)
        acc.fulfilled += sumQty(fulfilledItems)
        acc.value += sumValue(orderedItems)
        return acc
      },
      { orders: 0, items: 0, fulfilled: 0, value: 0 }
    )
    return totals
  }, [shipments])

  // Edit modal
  const [editingOrder, setEditingOrder] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [openingEditId, setOpeningEditId] = useState(null) // Track which order is opening

  // Bulk upload modal
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false)
  const openEditModal = (order) => {
    setOpeningEditId(order.id)
    // Small delay to show loading state before modal opens
    setTimeout(() => {
      setEditingOrder(order)
      setModalVisible(true)
      setOpeningEditId(null)
    }, 150)
  }

  // Fetching
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const jwt = localStorage.getItem("authToken") || ""
      if (!jwt) throw new Error("Not authenticated")
      const fromISO = formatISO(toDate(parseISO(activeFilters.from_date + "T00:00:00+05:30")))
      const toISO = formatISO(toDate(parseISO(activeFilters.to_date + "T23:59:59+05:30")))

      const merchantId = localStorage.getItem("merchantId")

      const body = {
        ...(merchantId && { merchant_id: merchantId }),
        ...(useDateFilter ? { from_date_utc: fromISO, to_date_utc: toISO } : {}),
        ...(activeFilters.order_statuses && activeFilters.order_statuses.length > 0 
          ? { order_statuses: activeFilters.order_statuses } 
          : {}),
        ...(activeFilters.confirmation_statuses && activeFilters.confirmation_statuses.length > 0 
          ? { confirmation_statuses: activeFilters.confirmation_statuses } 
          : {}),
        ...(activeFilters.marketplace_channel_order_number && { marketplace_channel_order_number: activeFilters.marketplace_channel_order_number }),
        sort_field: sort_field === "createdDateTime" ? "createdDateTime" : sort_field,
        sort_direction: sort_direction,
        limit,
        offset: (page - 1) * limit,
        fetch_only_count: false,
        filters: {
          ...(activeFilters.order_number && { order_number: activeFilters.order_number }),
        },
      }

      const res = await axios.post(API.getMarketplaceOrder(), body, { headers: { Authorization: jwt } })
      const data = res.data
      // New API response structure: { orders: [], total_count: number, ... }
      const items = Array.isArray(data) ? data : (data.orders || [])
      setShipments(items)
      setTotalCount(data.total_count ?? data.totalCount ?? (Array.isArray(data) ? data.length : 0))

      // When data changes, we keep existing carrier selections; if you want to reset,
      // you can uncomment next two lines:
      // setCarrierOptionsByKey({})
      // setCarrierSelectionByKey({})
    } catch (err) {
      console.error(err)
      const msg = err?.response?.data?.message || err?.message || "Failed to load orders"
      setError(msg)
      openModal("Couldn’t load orders", msg)
    } finally {
      setLoading(false)
    }
  }, [activeFilters, page, limit, sort_field, sort_direction, useDateFilter])

  useEffect(() => { fetchData() }, [fetchData])

  // Bulk: apply pickup to selected rows
  const handleApplyPickupToSelected = async () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage)
    if (selectedOnPage.length === 0) {
      openModal("No selection", "Please select at least one row to apply a pickup.")
      return
    }
    if (!selectedPickupId) {
      openModal("No pickup chosen", "Please choose a pickup location to apply.")
      return
    }

    const jwt = localStorage.getItem("authToken") || ""
    if (!jwt) {
      openModal("Not authenticated", "Please sign in again.")
      return
    }

    const pickup = pickupList.find((p) => p.pickup_address_id === selectedPickupId)
    if (!pickup) {
      openModal("Invalid pickup", "The selected pickup location could not be found.")
      return
    }

    const pickup_details = buildPickupDetails(pickup)

    // Set up abort controller (stored in ref)
    const controller = new AbortController()
    abortRef.current = controller

    setApplyingPickup(true)
    setApplyProgress({ done: 0, total: selectedOnPage.length })

    const tasks = selectedOnPage.map((order) => async () => {
      let attempt = 0
      while (true) {
        attempt++
        try {
          const headers = {
            Authorization: jwt,
            "Content-Type": "application/json",
            "Idempotency-Key": `pickup-${order.id || order.order_number}-${selectedPickupId}`,
          }

          // send only what backend expects; avoid mutating original order
          const body = JSON.parse(JSON.stringify(order))
          // Use new field name: market_place_pickup_details (API expects this, not pickup_details)
          body.market_place_pickup_details = { ...pickup_details }
          // clean up fields that should not be sent/updated
          delete body.market_place_pickup_details.pickup_address_id
          delete body.order_status
          // Remove old field name if it exists (should not be in payload)
          delete body.pickup_details

          await axios.put(API.updateMarketplaceOrders(), body, {
            headers,
            signal: controller.signal,
          })

          return { ok: true, orderId: order.id, orderNumber: order.order_number }
        } catch (err) {
          if (controller.signal.aborted) throw err
          if (isRetryable(err) && attempt < 5) {
            const ra = Number(err?.response?.headers?.["retry-after"])
            const delay = Number.isFinite(ra) ? ra * 1000 : backoff(attempt)
            await sleep(delay)
            continue
          }
          const msg = err?.response?.data?.message || err?.message || "unknown error"
          return { ok: false, orderId: order.id, orderNumber: order.order_number, error: msg }
        }
      }
    })

    try {
      const CONCURRENCY = 4
      const results = await runPool(tasks, CONCURRENCY, (done, total) =>
        setApplyProgress({ done, total })
      )
      const successes = results.filter((r) => r?.ok).length
      const failures = results.filter((r) => !r?.ok)
      if (failures.length === 0) {
        showToast(`Applied pickup to ${successes} order(s).`, "success")
      } else {
        openModal(
          "Completed with errors",
          `Updated ${successes} order(s), ${failures.length} failed:\n\n${failures
            .map((f) => `• ${f.orderNumber || f.orderId}: ${f.error}`)
            .join("\n")}`
        )
      }
    } finally {
      setApplyingPickup(false)
      abortRef.current = null
      await fetchData()
      setSelectedRows(new Set())
    }
  }

  const fetchPickupList = async () => {
    setLoadingPickups(true)
    try {
      const jwt = localStorage.getItem("authToken") || ""
      const parentClientId = localStorage.getItem("parentClientId") || localStorage.getItem("merchantId")
      if (!jwt) throw new Error("Missing auth token")
      if (!parentClientId) {
        console.warn("No parentClientId; cannot fetch pickup details")
        return
      }

      const url = API.getPickupDetails(parentClientId)
      const res = await axios.get(url, { headers: { Authorization: jwt, Accept: "application/json, text/plain, */*" } })
      if (!Array.isArray(res.data)) { setPickupList([]); return }
      const normalized = res.data.map((p) => ({
        pickup_address_id: p.pickup_address_id,
        pickup_address: p.pickup_address,
        pickup_city: p.pickup_city,
        pickup_state: p.pickup_state,
        pickup_postal_code: p.pickup_postal_code,
        pickup_country_code: p.pickup_country_code,
        pickup_poc: p.pickup_poc,
        pickup_organisation: p.pickup_organisation,
        pickup_landmark: p.pickup_landmark,
        pickup_poc_contact_number: p.pickup_poc_contact_number,
        pickup_poc_email: p.pickup_poc_email,
        pickup_district: p.pickup_district,
        pickup_address_type: p.pickup_address_type || null,
      }))
      setPickupList(normalized)
    } catch (e) {
      console.error("Failed to fetch pickup list", e)
    } finally {
      setLoadingPickups(false)
    }
  }
  useEffect(() => { fetchPickupList() }, [])

  // Row helpers
  const rowKey = (o) => String(o.id || o.order_number)
  const isSelectedOnPage = (o) => selectedRows.has(rowKey(o))
  const pageSelectedCount = shipments.filter(isSelectedOnPage).length
  const allOnPageSelected = shipments.length > 0 && shipments.every(isSelectedOnPage)

  const toggleSelectOne = (key) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }
  const toggleSelectAllOnPage = (checked) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (checked) shipments.forEach((o) => next.add(rowKey(o)))
      else shipments.forEach((o) => next.delete(rowKey(o)))
      return next
    })
  }
  useEffect(() => {
    const cb = headerCheckboxRef.current
    if (!cb) return
    cb.indeterminate = pageSelectedCount > 0 && !allOnPageSelected
  }, [pageSelectedCount, allOnPageSelected, shipments])

  // Label download
  const downloadShipmentLabelsServer = async (orderRefs) => {
    const jwt = localStorage.getItem("authToken")
    const merchantId = localStorage.getItem("merchantId")
    const POCAS = process.env.REACT_APP_POCAS_BASE_URL
    const MERGED = process.env.REACT_APP_BULK_MERGED_LABEL_DOWNLOAD_BY_COMMA_SEPARATED_ORDER_REFERENCE_NUMBER
    if (!jwt || !merchantId || !POCAS || !MERGED) throw new Error("Missing config/env for merged label API")
    const apiUrl = `${POCAS}${MERGED}${merchantId}`
    const payload = orderRefs.map((ref) => ({ order_reference_number: ref }))
    const { data } = await axios.post(apiUrl, payload, { headers: { Authorization: jwt, "Content-Type": "application/json" } })
    const url = data?.label_csv_file_url
    if (!url) throw new Error("No label url in response")
    window.open(url, "_blank", "noopener,noreferrer")
  }
  const extractOrderRefs = (order) => {
    const refs = []
    const shipmentDetails = order?.shipment_details || order?.market_place_shipment_details
    const direct = shipmentDetails?.order_details?.order_reference_number || order?.order_reference_number
    if (direct) refs.push(String(direct))
    getFulfilledPkgs(order).forEach((p) => { if (p?.order_reference_number) refs.push(String(p.order_reference_number)) })
    return Array.from(new Set(refs))
  }
  const collectLabelUrls = (order) => {
    const urls = new Set()
    const fields = ["order_label_url", "label_url", "label_pdf_url", "shipping_label_url", "shipment_label_url"]
    getFulfilledPkgs(order).forEach((p) => {
      for (const f of fields) { const u = p?.[f]; if (u) { urls.add(u); break } }
    })
    fields.forEach((f) => { const u = order?.[f]; if (u) urls.add(u) })
    return Array.from(urls)
  }
  const handleDownloadSelectedLabels = async () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage)
    if (selectedOnPage.length === 0) { openModal("No selection", "Select at least one row to download labels."); return }
    setLoadingDownloadSelected(true)
    try {
      const orderRefs = Array.from(new Set(selectedOnPage.flatMap(extractOrderRefs))).filter(Boolean)
      if (orderRefs.length) { await downloadShipmentLabelsServer(orderRefs); return }
      throw new Error("No order refs found for merged download")
    } catch (err) {
      const urls = Array.from(new Set(selectedOnPage.flatMap(collectLabelUrls)))
      if (urls.length === 0) {
        openModal("No labels found", "Selected order is not fulfilled yet. Please fulfill/place this order to download the label.")
      } else {
        urls.forEach((u, idx) => {
          const a = document.createElement("a")
          a.href = u; a.target = "_blank"; a.rel = "noopener noreferrer"; a.download = ""
          document.body.appendChild(a)
          setTimeout(() => { a.click(); document.body.removeChild(a) }, idx * 120)
        })
        const skipped = selectedOnPage.length - urls.length
        if (skipped > 0) setTimeout(() => openModal("Some labels skipped", `${skipped} selected ${skipped === 1 ? "row has" : "rows have"} no label URL and were skipped.`), urls.length * 120 + 100)
      }
    } finally { setLoadingDownloadSelected(false) }
  }

  // Filters
  const handleInput = (e) => {
    const { name, value } = e.target
    setInputFilters((f) => ({ ...f, [name]: value }))
    if (SPECIFIC_FIELDS.includes(name)) {
      const hasValue = String(value || "").trim().length > 0
      if (hasValue) setUseDateFilter(false)
    }
  }

  const getInvoiceValue = (o) => {
    const shipmentDetails = o?.shipment_details || o?.market_place_shipment_details
    const raw =
      shipmentDetails?.order_details?.order_invoice_details?.invoice_value

    const num =
      typeof raw === "string" ? parseFloat(raw) : Number(raw || 0)

    return isNaN(num) ? 0 : num
  }

  useEffect(() => {
    const anySpecific = SPECIFIC_FIELDS.some((k) => String(inputFilters[k] || "").trim() !== "")
    if (!anySpecific && !useDateFilter) setUseDateFilter(true)
  }, [inputFilters, useDateFilter])

  const handleRangeChange = ([from, to]) => {
    setInputFilters((f) => ({ ...f, from_date: from.toLocaleDateString("en-CA"), to_date: to.toISOString().slice(0, 10) }))
  }
  const applyFilters = () => { setActiveFilters(inputFilters); setPage(1) }
  const resetFilters = () => {
    const now = new Date()
    const from = new Date(now)
    from.setDate(now.getDate() - 30)
    const defs = {
      from_date: from.toLocaleDateString("en-CA"),
      to_date: now.toLocaleDateString("en-CA"),
      order_number: "",
      marketplace_channel_order_number: "",
      order_statuses: [],
      confirmation_statuses: [],
    }
    setInputFilters(defs)
    setActiveFilters(defs)
    setUseDateFilter(true)
    setPage(1)
  }

  // Table interactions
  const toggleRow = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }))
  const handleSort = (field) => { const dir = sort_field === field && sort_direction === "ASC" ? "DESC" : "ASC"; setSortField(field); setSortDir(dir); setPage(1) }
  const nextPage = () => { if (shipments.length === limit) setPage((p) => p + 1) }
  const prevPage = () => { if (page > 1) setPage((p) => p - 1) }
  const handleLimitChange = (e) => { setLimit(+e.target.value); setPage(1) }

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/") { e.preventDefault(); const el = document.querySelector("input[name='order_number']"); el?.focus() }
      if (e?.key?.toLowerCase() === "r" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); fetchData() }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [fetchData])

  // ─── Manual courier eligibility map (only PENDING + has pending qty) ────────
  const manualCarrierEligibilityMap = useMemo(() => {
    const m = new Map()
    shipments.forEach((o) => {
      const orderedPkgs = getOrderedPkgs(o)
      const fulfilledPkgs = getFulfilledPkgs(o)
      const orderedItems = flattenItems(orderedPkgs)
      const fulfilledItems = flattenItems(fulfilledPkgs)
      const orderedQty = sumQty(orderedItems)
      const fulfilledQty = sumQty(fulfilledItems)
      const isPendingStatus = String(o?.order_status || "").toUpperCase() === "PENDING"
      const canCreatePending = isPendingStatus && orderedQty > fulfilledQty
      m.set(rowKey(o), canCreatePending)
    })
    return m
  }, [shipments])

  const selectedHasIneligibleForAction = useMemo(() => {
    if (!selectedRows || selectedRows.size === 0) return false
    for (let key of selectedRows) {
      if (!manualCarrierEligibilityMap.get(key)) return true
    }
    return false
  }, [selectedRows, manualCarrierEligibilityMap])

  const performManualCourierFetch = async (eligibleOrders) => {
    if (!eligibleOrders || eligibleOrders.length === 0) return

    let allocatedCarriers = []
    const authToken = localStorage.getItem("authToken")
    const allocatedCarriersRaw = localStorage.getItem("allocatedCarriers")

    if (!authToken) {
      openModal(
        "Missing authentication",
        "Auth token is missing. Please sign in again."
      )
      return
    }

    try {
      allocatedCarriers = JSON.parse(allocatedCarriersRaw || "[]") || []
    } catch (e) {
      console.error("Invalid allocatedCarriers in localStorage", e)
      openModal(
        "Carrier configuration error",
        "Carrier information is not available or invalid."
      )
      return
    }

    if (!Array.isArray(allocatedCarriers) || allocatedCarriers.length === 0) {
      openModal(
        "No carriers configured",
        "No allocated carriers found. Please configure carriers first."
      )
      return
    }

    const carrierIdsToEvaluate = allocatedCarriers.map(
      (c) => c.carrier_partner_id
    )
    const correlationIdMap = new Map()

    const payload = eligibleOrders.map((order) => {
      const key = rowKey(order)
      const correlation_id = uuidv4()
      correlationIdMap.set(correlation_id, key)

      const orderedPkgs = getOrderedPkgs(order)
      const primaryPkg = orderedPkgs[0] || {}

      const weightInGrams = Number(primaryPkg?.weight?.product_weight || 0)
      const lengthInCm = Number(primaryPkg?.dimensions?.product_length || 0)
      const breadthInCm = Number(primaryPkg?.dimensions?.product_width || 0)
      const heightInCm = Number(primaryPkg?.dimensions?.product_height || 0)

      const shipmentDetails = order?.shipment_details || order?.market_place_shipment_details
      const shipmentType =
        order?.shipment_order_type ||
        shipmentDetails?.order_details?.shipment_order_type ||
        "FORWARD"

      const paymentMode =
        shipmentDetails?.order_details?.order_type || null

      const invoiceValue = getInvoiceValue(order)
      const pickupDetailsForPincode = order?.pickup_details || order?.market_place_pickup_details
      const pickupPincode = pickupDetailsForPincode?.pickup_postal_code
      const dropDetailsForPincode = order?.drop_details || order?.market_place_customer_drop_details
      const dropPincode = dropDetailsForPincode?.drop_postal_code

      return {
        correlation_id,
        carrier_options_calculation_context: {
          shipment_type: shipmentType,
          payment_mode: paymentMode,
          invoice_value: invoiceValue,
          rvp_with_quality_check: false,
          weight_in_grams: weightInGrams,
          length_in_cm: lengthInCm,
          breadth_in_cm: breadthInCm,
          height_in_cm: heightInCm,
          pickup_pincode: pickupPincode,
          drop_pincode: dropPincode,
        },
        carrier_partner_ids_to_evaluate: carrierIdsToEvaluate,
      }
    })

    const baseUrl =
      `${process.env.REACT_APP_OMS_BASE_URL}` +
      `${process.env.REACT_APP_RETRIEVE_CARRIER_OPTIONS_IN_BATCH}`

    // mark only these keys as loading
    const loadingSetForThisCall = new Set(
      eligibleOrders.map((o) => rowKey(o))
    )
    setLoadingCarrierKeys((prev) => {
      const next = new Set(prev)
      loadingSetForThisCall.forEach((k) => next.add(k))
      return next
    })

    try {
      const res = await axios.post(baseUrl, payload, {
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
      })

      const updatedOptions = {}
      const updatedSelection = {}

      for (const result of res.data || []) {
        const {
          correlation_id,
          carrier_options_calculation_result_by_carrier_partner_id,
        } = result || {}

        const key = correlationIdMap.get(correlation_id)
        if (!key) continue

        const map =
          carrier_options_calculation_result_by_carrier_partner_id || {}
        const serviceableOptions = Object.entries(map)
          .filter(([_, val]) => val && val.serviceable)
          .map(([id, val]) => ({
            id,
            name: val.carrier_partner_public_name,
            price: val.total_amount_to_charge_after_tax,
          }))

        updatedOptions[key] = serviceableOptions

        // Auto-pick cheapest for each order (same behavior as before)
        if (serviceableOptions.length > 0) {
          const cheapest = serviceableOptions.reduce(
            (min, o) => (o.price < min.price ? o : min),
            serviceableOptions[0]
          )
          updatedSelection[key] = cheapest.id
        }
      }

      setCarrierOptionsByKey((prev) => ({ ...prev, ...updatedOptions }))
      setCarrierSelectionByKey((prev) => ({ ...prev, ...updatedSelection }))

      const countWithOptions = Object.keys(updatedOptions).length
      if (countWithOptions === 0) {
        openModal(
          "No serviceable couriers",
          "No serviceable courier options were found for the selected orders."
        )
      }
    } catch (err) {
      console.error("Manual courier selection failed:", err)
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch courier options."
      openModal("Courier options failed", msg)
    } finally {
      // clear loading only for these keys (so multiple calls can coexist)
      setLoadingCarrierKeys((prev) => {
        const next = new Set(prev)
        loadingSetForThisCall.forEach((k) => next.delete(k))
        return next
      })
    }
  }

  const handleManualCourierForSelected = async () => {
    const selectedOnPage = shipments.filter(isSelectedOnPage)
    if (selectedOnPage.length === 0) {
      openModal("No selection", "Please select at least one order.")
      return
    }

    const eligibleOrders = selectedOnPage.filter((o) =>
      manualCarrierEligibilityMap.get(rowKey(o))
    )

    if (eligibleOrders.length === 0) {
      openModal(
        "No eligible orders",
        "Manual courier can be applied only on PENDING orders with pending quantity."
      )
      return
    }

    await performManualCourierFetch(eligibleOrders)
  }

  const handleManualCourierForOrder = async (order) => {
    const key = rowKey(order)
    const isEligible = manualCarrierEligibilityMap.get(key)

    if (!isEligible) {
      openModal(
        "Not eligible for manual courier",
        "Manual courier can be applied only on PENDING orders with pending quantity."
      )
      return
    }

    await performManualCourierFetch([order])
  }


  const handleUseRecommendationForSelected = () => {
    if (!selectedRows || selectedRows.size === 0) return
    setCarrierSelectionByKey((prev) => {
      const next = { ...prev }
      selectedRows.forEach((key) => {
        delete next[key]
      })
      return next
    })
    setCarrierOptionsByKey((prev) => {
      const next = { ...prev }
      selectedRows.forEach((key) => {
        delete next[key]
      })
      return next
    })
  }

  // Open CSV column selection modal
  const handleOpenCSVModal = () => {
    if (selectedRows.size === 0) {
      showToast("No orders selected to export", "error")
      return
    }
    setShowCSVColumnModal(true)
  }

  // Download selected orders as CSV with selected columns
  const handleDownloadCSV = async (columnsToExport = null) => {
    if (selectedRows.size === 0) {
      showToast("No orders selected to export", "error")
      return
    }

    // Use provided columns or default to selected columns
    const columns = columnsToExport || Array.from(selectedCSVColumns)
    if (columns.length === 0) {
      showToast("Please select at least one column to export", "error")
      return
    }

    setDownloadingCSV(true)
    setShowCSVColumnModal(false)
    try {
      const jwt = localStorage.getItem("authToken") || ""
      if (!jwt) throw new Error("Not authenticated")

      // Get selected order IDs
      const selectedIds = Array.from(selectedRows)
      const selectedOnPage = shipments.filter((o) => selectedRows.has(rowKey(o)))
      
      // If we have more selected than on current page, fetch all selected orders
      let ordersToExport = selectedOnPage
      
      if (selectedIds.length > selectedOnPage.length) {
        // Fetch all selected orders by making API calls with order numbers
        // We'll use the current filters and fetch in batches
        const merchantId = localStorage.getItem("merchantId")
        const fromISO = formatISO(toDate(parseISO(activeFilters.from_date + "T00:00:00+05:30")))
        const toISO = formatISO(toDate(parseISO(activeFilters.to_date + "T23:59:59+05:30")))

        // Fetch all orders matching current filters (same as select all)
        const body = {
          ...(merchantId && { merchant_id: merchantId }),
          ...(useDateFilter ? { from_date_utc: fromISO, to_date_utc: toISO } : {}),
          ...(activeFilters.order_statuses && activeFilters.order_statuses.length > 0 
            ? { order_statuses: activeFilters.order_statuses } 
            : {}),
          ...(activeFilters.confirmation_statuses && activeFilters.confirmation_statuses.length > 0 
            ? { confirmation_statuses: activeFilters.confirmation_statuses } 
            : {}),
          ...(activeFilters.marketplace_channel_order_number && { marketplace_channel_order_number: activeFilters.marketplace_channel_order_number }),
          sort_field: sort_field === "createdDateTime" ? "createdDateTime" : sort_field,
          sort_direction: sort_direction,
          limit: 10000,
          offset: 0,
          fetch_only_count: false,
          filters: {
            ...(activeFilters.order_number && { order_number: activeFilters.order_number }),
          },
        }

        const res = await axios.post(API.getMarketplaceOrder(), body, { headers: { Authorization: jwt } })
        const data = res.data
        const allOrders = Array.isArray(data) ? data : (data.orders || [])
        
        // Filter to only selected orders
        ordersToExport = allOrders.filter((o) => selectedRows.has(rowKey(o)))
      }

      // Get selected column labels and keys in order (ensure columns is an array)
      const columnsArray = Array.isArray(columns) ? columns : Array.from(columns)
      // Filter and maintain order from allCSVColumns
      const selectedColumns = allCSVColumns.filter(col => columnsArray.includes(col.key))
      const headers = selectedColumns.map(col => col.label)
      // Use the same order for data extraction
      const columnKeys = selectedColumns.map(col => col.key)

      // Map column keys to their data extractors
      const getColumnValue = (order, columnKey) => {
        const pickupDetails = order.pickup_details || order.market_place_pickup_details || {}
        const dropDetails = order.drop_details || order.market_place_customer_drop_details || {}
        const shipmentDetails = order.shipment_details || order.market_place_shipment_details || {}
        const orderDetails = shipmentDetails?.order_details || {}
        const packages = shipmentDetails?.shipment_packages || []
        const fulfilledPackages = shipmentDetails?.shipment_fulfilled_packages || []

        const valueMap = {
          "order_number": order.order_number || "",
          "channel_order_id": order.marketplace_channel_order_number || "",
          "order_status": order.order_status || "",
          "confirmation_status": order.confirmation_details?.confirmation_status || "Not Sent",
          "store_id": order.store_id || "",
          "marketplace_channel": order.market_place_channel_name || "",
          "shipment_type": order.shipment_order_type || "",
          "created_date_time": order.created_date_time || "",
          "updated_date_time": order.updated_date_time || "",
          "pickup_organisation": pickupDetails.pickup_organisation || "",
          "pickup_address": pickupDetails.pickup_address || "",
          "pickup_city": pickupDetails.pickup_city || "",
          "pickup_state": pickupDetails.pickup_state || "",
          "pickup_postal_code": pickupDetails.pickup_postal_code || "",
          "drop_poc": dropDetails.drop_poc || "",
          "drop_address": dropDetails.drop_address || "",
          "drop_city": dropDetails.drop_city || "",
          "drop_state": dropDetails.drop_state || "",
          "drop_postal_code": dropDetails.drop_postal_code || "",
          "order_type": orderDetails.order_type || "",
          "cod_amount": orderDetails.cod_amount?.value || "",
          "invoice_value": orderDetails.order_invoice_details?.invoice_value || "",
          "invoice_currency": orderDetails.order_invoice_details?.invoice_currency || "",
          "total_packages": packages.length || 0,
          "fulfilled_packages": fulfilledPackages.length || 0
        }

        return valueMap[columnKey] ?? ""
      }

      const rows = ordersToExport.map((o) => {
        // Use columnKeys (from selectedColumns) to ensure correct order and no extra elements
        return columnKeys.map(colKey => {
          const value = getColumnValue(o, colKey)
          return value === null || value === undefined ? "" : String(value)
        })
      })

      // Escape CSV values
      const escape = (val) => {
        if (val === null || val === undefined) return ""
        const str = String(val)
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      }

      // Build CSV content - ensure proper formatting
      const csvRows = [
        headers.join(","),
        ...rows.map((row) => {
          // Map each value and escape it, ensuring no empty values at start
          const escapedRow = row.map(val => escape(val))
          return escapedRow.join(",")
        })
      ]
      const csvContent = csvRows.join("\r\n")

      // Trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0]
      const a = document.createElement("a")
      a.href = url
      a.download = `channel_orders_${timestamp}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      showToast(`CSV exported with ${ordersToExport.length} order(s)`, "success")
    } catch (err) {
      console.error(err)
      showToast("Failed to export CSV", "error")
    } finally {
      setDownloadingCSV(false)
    }
  }

  // Single order: create fulfillment
  const createShipment = async (order) => {
    const orderId = order.id
    const key = rowKey(order)
    const chosenCarrierId = carrierSelectionByKey[key] || null  // ← manual courier choice

    setCreatingIds((prev) => [...prev, orderId])
    try {
      const required = [
        { value: order.order_number, label: "Order Number" },
        { value: order.store_id, label: "Store ID" },
        { value: order.market_place_channel_name, label: "Marketplace" },
      ]
      const missing = required
        .filter(
          (f) =>
            f.value === null ||
            f.value === undefined ||
            String(f.value).trim() === ""
        )
        .map((f) => f.label)
      if (missing.length) {
        showToast(`Cannot create shipment. Missing: ${missing.join(", ")}`, "error")
        return
      }

      const jwt = localStorage.getItem("authToken") || ""
      if (!jwt) {
        showToast("Not authenticated", "error")
        throw new Error("Not authenticated")
      }

      const payload = {
        order_number: order.order_number,
        market_place_channel_name: order.market_place_channel_name,
        ...(order.store_id ? { store_id: order.store_id } : {}),
        // If manual courier selected, send it to backend
        ...(chosenCarrierId ? { carrier_partner_id: chosenCarrierId } : {}),
      }

      await axios.post(API.fullfillmentMarketplaceOrders(), payload, {
        headers: { Authorization: jwt, "Content-Type": "application/json" },
      })
      showToast("Fulfillment creation triggered", "success")
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "unknown"
      showToast(`Create shipment failed: ${msg}`, "error")
    } finally {
      setCreatingIds((prev) => prev.filter((id) => id !== orderId))
    }
  }

  const cancelOrder = async (order, reason) => {
    const orderId = order.id || order.order_number
    const hasAnyFulfilled = sumQty(flattenItems(getFulfilledPkgs(order))) > 0
    if (hasAnyFulfilled) { showToast("This order already has fulfilled items and cannot be canceled.", "error"); return }

    const required = [
      { value: order.order_number, label: "Order Number" },
      { value: order.market_place_channel_name, label: "Marketplace" },
      { value: order.store_id, label: "Store ID" },
    ]
    const missing = required.filter(f => f.value === null || f.value === undefined || String(f.value).trim() === "").map(f => f.label)
    if (missing.length) { showToast(`Cannot cancel order. Missing: ${missing.join(", ")}`, "error"); return }

    const jwt = localStorage.getItem("authToken") || ""
    if (!jwt) { showToast("Not authenticated", "error"); return }

    const cancelReason = String(reason || "").trim(); if (!cancelReason) return

    setCancellingIds((prev) => [...prev, orderId])
    try {
      const payload = { order_number: order.order_number, market_place_channel_name: order.market_place_channel_name, store_id: order.store_id, cancel_reason: cancelReason }
      await axios.post(API.cancelMarketplaceOrder(), payload, { headers: { Authorization: jwt, "Content-Type": "application/json" } })
      showToast("Order cancellation requested.", "success")
      await fetchData()
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "unknown"
      showToast(`Cancel order failed: ${msg}`, "error")
    } finally { setCancellingIds((prev) => prev.filter((id) => id !== orderId)) }
  }

  const densityRow = density === "compact" ? "py-1.5" : "py-2.5"

  // ─── Render ───────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 relative" style={{ overflowX: 'hidden', width: '100%', maxWidth: '100vw' }}>
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ overflowX: 'hidden' }}>
        <div className="absolute top-0 -left-4 w-96 h-96 bg-gradient-to-br from-indigo-200/20 to-transparent rounded-full blur-3xl animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-gradient-to-bl from-violet-200/20 to-transparent rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-gradient-to-tr from-sky-200/20 to-transparent rounded-full blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="mx-auto w-full px-2 sm:px-2 lg:px-2 py-2 relative z-10" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        {/* Enhanced Page header with stats */}
        <div className="mb-6 w-full" style={{ maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
          <div className="flex flex-wrap items-start justify-between gap-6 mb-6 w-full" style={{ maxWidth: '100%' }}>
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                  <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round"/>
                    <rect x="14" y="3" width="7" height="7" strokeWidth="2" strokeLinecap="round"/>
                    <rect x="14" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round"/>
                    <rect x="3" y="14" width="7" height="7" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold tracking-tight text-gray-900">
                    Channel Orders
                  </h3>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              <button 
                onClick={fetchData} 
                className="group inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 hover:ring-slate-300 active:bg-slate-100 font-medium transition-all duration-200 shadow-sm hover:shadow"
                title="Refresh (⌘/Ctrl+R)"
              >
                <Icon.Refresh className="h-4 w-4 group-hover:rotate-180 transition-transform duration-500" /> 
                Refresh
              </button>
              <div className="relative group">
                <select 
                  value={density} 
                  onChange={(e) => setDensity(e.target.value)} 
                  className="appearance-none pl-4 pr-10 py-2.5 rounded-xl bg-white ring-1 ring-slate-200 text-sm focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none font-medium cursor-pointer transition-all duration-200 shadow-sm hover:shadow"
                >
                  <option value="comfortable">Comfortable View</option>
                  <option value="compact">Compact View</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400 group-hover:text-slate-600 transition-colors">
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0l-4.24-4.24a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-medium transition-all duration-200 shadow-sm hover:shadow ${
                  showFilters 
                    ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-500 hover:to-violet-500'
                    : 'bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
                title="Toggle filters"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Filters
              </button>
            </div>
          </div>

          {/* Quick stats cards with CSV Export and Bulk Upload */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 animate-fadeInUp">
            {/* Stats Cards */}
            {[
              { label: 'Total Orders', value: stats.orders, icon: '📦', color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50' },
              { label: 'Total Items', value: stats.items, icon: '📋', color: 'from-violet-500 to-purple-500', bg: 'bg-violet-50' },
              { label: 'Total Value', value: formatINR(stats.value), icon: '💰', color: 'from-amber-500 to-orange-500', bg: 'bg-amber-50' },
            ].map((stat, idx) => (
              <div 
                key={stat.label}
                className="group relative bg-white/80 backdrop-blur-sm rounded-xl p-3 ring-1 ring-slate-200/50 hover:ring-slate-300 hover:shadow-lg transition-all duration-300 overflow-hidden"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">{stat.icon}</span>
                    <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center ring-1 ring-slate-200/50`}>
                      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${stat.color}`}></div>
                    </div>
                  </div>
                  <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide mb-0.5">{stat.label}</div>
                  <div className={`text-base font-bold text-gray-900`}>
                    {stat.value}
                  </div>
                </div>
              </div>
            ))}
            
            {/* CSV Export Card - 4th card */}
            <div 
              onClick={handleOpenCSVModal}
              className={`group relative bg-white/80 backdrop-blur-sm rounded-xl p-3 ring-1 ring-slate-200/50 hover:ring-emerald-300 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer ${downloadingCSV || selectedRows.size === 0 ? 'opacity-60 cursor-not-allowed' : ''}`}
              style={{ animationDelay: '300ms' }}
              title={selectedRows.size === 0 ? "Select at least one order to export" : "Configure and download CSV"}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">
                    {downloadingCSV ? (
                      <span className="inline-block w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <Icon.Download className="h-4 w-4 text-emerald-600" />
                    )}
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center ring-1 ring-slate-200/50">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500"></div>
                  </div>
                </div>
                <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide mb-0.5">Export CSV</div>
                <div className="text-base font-bold text-gray-900">
                  {downloadingCSV ? 'Preparing...' : selectedRows.size > 0 ? `${selectedRows.size} Selected` : 'Select Orders'}
                </div>
              </div>
            </div>

            {/* Bulk Upload Card - 5th card */}
            <div 
              onClick={() => setBulkUploadModalOpen(true)}
              className="group relative bg-white/80 backdrop-blur-sm rounded-xl p-3 ring-1 ring-slate-200/50 hover:ring-indigo-300 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
              style={{ animationDelay: '400ms' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-violet-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">📤</span>
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center ring-1 ring-slate-200/50">
                    <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500"></div>
                  </div>
                </div>
                <div className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide mb-0.5">Bulk Upload</div>
                <div className="text-base font-bold text-gray-900">
                  Upload CSV
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ENHANCED FILTER CARD */}
        <div 
          className={`transition-all duration-500 w-full ${
            showFilters ? 'max-h-[800px] opacity-100 mb-6' : 'max-h-0 opacity-0 mb-0'
          }`}
          style={{ overflow: 'visible', position: 'relative', zIndex: showFilters ? 1 : 'auto', maxWidth: '100%', boxSizing: 'border-box' }}
        >
          <div className={`${panel} p-6 shadow-xl ring-1 ring-slate-200/50 relative w-full`} style={{ overflow: 'visible', maxWidth: '100%', boxSizing: 'border-box' }}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center ring-1 ring-indigo-200/50">
                  <svg className="h-5 w-5 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Filter & Search</h3>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 ring-1 ring-slate-200">
                  <kbd className="px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200 font-mono text-[10px] font-semibold">/</kbd>
                  <span className="text-slate-600">Focus search</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 ring-1 ring-slate-200">
                  <kbd className="px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200 font-mono text-[10px] font-semibold">⌘R</kbd>
                  <span className="text-slate-600">Refresh</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3 w-full" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
              <div className="w-full sm:w-64">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Created Date</label>
                  {useDateFilter && (
                    <button
                      type="button"
                      onClick={() => setUseDateFilter(false)}
                      className="text-[11px] text-rose-600 hover:text-rose-700 font-semibold underline decoration-dotted transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {useDateFilter ? (
                  <div className="date-compact rounded-xl ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-indigo-300 shadow-sm hover:ring-slate-300 transition-all h-[42px] bg-white">
                    <CustomDateRangePicker
                      selectedRange={[
                        parseISO(inputFilters.from_date),
                        parseISO(inputFilters.to_date),
                      ]}
                      onRangeChange={handleRangeChange}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setUseDateFilter(true)}
                    className="w-full h-[42px] rounded-xl ring-2 ring-dashed ring-slate-300 text-sm text-slate-600 hover:ring-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition-all duration-200 font-medium"
                    title="Add Created Date range"
                  >
                    + Add Date Range
                  </button>
                )}
              </div>

              {[
                { name: "order_number", label: "Order Number", placeholder: "Search order #...", icon: Icon.Search },
                { name: "marketplace_channel_order_number", label: "Channel Order #", placeholder: "Enter channel order #...", icon: Icon.Search },
              ].map((fld) => (
                <div key={fld.name} className="w-full sm:w-40 lg:w-44">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">{fld.label}</label>
                  <div className="relative group">
                    <input
                      type="text"
                      name={fld.name}
                      value={inputFilters[fld.name]}
                      onChange={handleInput}
                      placeholder={fld.placeholder}
                      className={`${inputCls} pl-10 h-[42px] text-sm hover:ring-slate-300 transition-all duration-200 group-hover:shadow-sm`}
                    />
                    <fld.icon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                  </div>
                </div>
              ))}


              {/* Order Statuses Multi-select with Checkboxes */}
              <MultiSelectDropdown
                label="Order Status"
                options={[
                  "PENDING",
                  "CONFIRMED",
                  "PARTIALLY_FULFILLED",
                  "FULFILLED",
                  "CANCELED",
                  "LABEL_PRINTED",
                  "LABEL_PURCHASED",
                  "PICKUP_DONE",
                  "IN_TRANSIT",
                  "OUT_FOR_DELIVERY",
                  "ATTEMPTED_DELIVERY",
                  "DELIVERED",
                  "READY_FOR_PICKUP",
                  "FAILURE",
                  "RETURNED",
                ]}
                selected={inputFilters.order_statuses || []}
                onChange={(selected) => setInputFilters((f) => ({ ...f, order_statuses: selected }))}
              />

              {/* Confirmation Statuses Multi-select with Checkboxes */}
              <MultiSelectDropdown
                label="Confirmation Status"
                options={["CONFIRMED", "PENDING", "CANCELLED"]}
                selected={inputFilters.confirmation_statuses || []}
                onChange={(selected) => setInputFilters((f) => ({ ...f, confirmation_statuses: selected }))}
              />

              <div className="flex items-end gap-2 ml-auto">
                <button 
                  className={`${btn.primary} h-[42px] px-5 shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200`}
                  onClick={applyFilters}
                >
                  <Icon.Search className="h-4 w-4" /> Apply Filters
                </button>
                <button 
                  className={`${btn.danger} h-[42px] px-5 shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200`}
                  onClick={resetFilters}
                >
                  <Icon.X className="h-4 w-4" /> Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ENHANCED BULK ACTIONS TILE */}
        <div className={`${panel} p-5 mb-6 shadow-xl ring-1 ring-slate-200/50 overflow-hidden relative w-full`} style={{ maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-50/50 via-violet-50/30 to-transparent opacity-50"></div>
          <div className="relative">
            <div className="flex flex-col lg:flex-row lg:items-center gap-5">
              {/* Header - Enhanced */}
              <div className="flex-shrink-0">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                    <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-base font-bold text-slate-900">Bulk Actions</div>
                    <div className="text-xs text-slate-600">
                      <span className="font-semibold text-indigo-600">{pageSelectedCount}</span> {pageSelectedCount === 1 ? 'order' : 'orders'} selected
                    </div>
                  </div>
                </div>
              </div>

              {/* Vertical Divider - Desktop Only */}
              <div className="hidden lg:block h-12 w-px bg-gradient-to-b from-transparent via-slate-300 to-transparent"></div>

              {/* Actions Row - Enhanced */}
              <div className="flex flex-wrap items-center gap-3 flex-1">
                {/* Pickup Dropdown - Enhanced */}
                <div className="flex-1 min-w-[280px] relative group">
                  <select
                    value={selectedPickupId}
                    onChange={(e) => setSelectedPickupId(e.target.value)}
                    disabled={loadingPickups || applyingPickup}
                    className="w-full px-4 py-2.5 text-sm rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-300 hover:ring-slate-300 outline-none bg-white appearance-none transition-all duration-200 font-medium cursor-pointer shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">🏭 Select Pickup Location</option>
                    {pickupList.map((p) => {
                      const org = p.pickup_organisation
                      const addrParts = [
                        p.pickup_address,
                        p.pickup_city,
                        p.pickup_state,
                        p.pickup_postal_code,
                        p.pickup_country_code,
                      ].filter(Boolean)

                      const fullLabel = org
                        ? `${org} — ${addrParts.join(", ")}`
                        : addrParts.join(", ") || "Unnamed pickup"

                      return (
                        <option key={p.pickup_address_id} value={p.pickup_address_id}>
                          {fullLabel}
                        </option>
                      )
                    })}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400 group-hover:text-slate-600 transition-colors">
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0l-4.24-4.24a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>

                {/* Action Buttons - Enhanced with better visual hierarchy */}
                <button
                  onClick={handleApplyPickupToSelected}
                  disabled={applyingPickup || !selectedPickupId || pageSelectedCount === 0}
                  className={`${btn.success} text-sm py-2.5 px-4 whitespace-nowrap shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200`}
                  title={!selectedPickupId ? "Choose a pickup location first" : pageSelectedCount === 0 ? "Select at least one row" : "Apply pickup to selected orders"}
                >
                  📍 Apply Pickup
                </button>

                <button
                  onClick={handleDownloadSelectedLabels}
                  disabled={pageSelectedCount === 0 || loadingDownloadSelected}
                  className={`${btn.info} text-sm py-2.5 px-4 whitespace-nowrap shadow-lg shadow-sky-500/30 hover:shadow-xl hover:shadow-sky-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200`}
                  title={pageSelectedCount === 0 ? "Select at least one row" : "Download labels"}
                >
                  {loadingDownloadSelected ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Preparing…
                    </>
                  ) : (
                    <>📄 Download Label</>
                  )}
                </button>

                <button
                  onClick={handleManualCourierForSelected}
                  disabled={pageSelectedCount === 0 || selectedHasIneligibleForAction}
                  className={`${btn.subtle} text-sm py-2.5 px-4 whitespace-nowrap hover:ring-2 hover:ring-indigo-200 transition-all duration-200`}
                  title={
                    pageSelectedCount === 0
                      ? "Select at least one PENDING order"
                      : selectedHasIneligibleForAction
                        ? "All selected orders must be PENDING with pending quantity"
                        : "Fetch courier options for selected orders"
                  }
                >
                  🚚 Manual Courier
                </button>

                <button
                  onClick={handleUseRecommendationForSelected}
                  disabled={pageSelectedCount === 0}
                  className={`${btn.ghost} text-sm py-2.5 px-4 whitespace-nowrap transition-all duration-200`}
                  title="Clear manual courier selection and use recommendation rule"
                >
                  ✨ Use Recommendation
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ENHANCED TABLE - Grows with rows, only scrolls horizontally when needed */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/60 overflow-hidden ring-1 ring-slate-200/50 w-full" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
          <div 
            className={`${density === "compact" ? "density-compact" : ""}`} 
            style={{ 
              width: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              overflowY: 'visible',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {loading ? (
              <div className="divide-y divide-slate-100">
                {Array.from({ length: limit }).map((_, idx) => (
                  <div key={idx} className="px-6 py-5 animate-pulse" style={{ animationDelay: `${idx * 50}ms` }}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl"></div>
                      <div className="flex-1 space-y-3">
                        <div className="h-4 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 rounded-lg w-1/3 animate-shimmer" style={{ backgroundSize: '200% 100%' }}></div>
                        <div className="flex gap-2">
                          <div className="h-3 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 rounded-md w-24 animate-shimmer" style={{ backgroundSize: '200% 100%' }}></div>
                          <div className="h-3 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 rounded-md w-20 animate-shimmer" style={{ backgroundSize: '200% 100%', animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="py-20 text-center px-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-rose-50 to-red-50 text-rose-600 mb-6 ring-4 ring-rose-100 shadow-xl shadow-rose-500/10">
                  <svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                    <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="16" r="0.5" fill="currentColor" strokeWidth="1"/>
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Something went wrong</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">{error}</p>
                <button 
                  className={`${btn.primary} shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:shadow-indigo-500/40 hover:scale-105 active:scale-95`}
                  onClick={fetchData}
                >
                  <Icon.Refresh className="h-4 w-4" /> Try Again
                </button>
              </div>
            ) : shipments.length === 0 ? (
              <div className="py-24 text-center px-6">
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-3xl bg-gradient-to-br from-slate-50 to-indigo-50 mb-8 ring-4 ring-slate-100 shadow-2xl relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 to-transparent animate-pulse"></div>
                  <span className="text-3xl relative z-10">📦</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">No orders found</h3>
                <p className="text-slate-600 mb-8 max-w-md mx-auto">
                  We couldn't find any orders matching your filters. Try adjusting your search criteria or date range.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button 
                    className={`${btn.primary} shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:shadow-indigo-500/40 hover:scale-105 active:scale-95`}
                    onClick={resetFilters}
                  >
                    <Icon.X className="h-4 w-4" /> Clear All Filters
                  </button>
                  <button 
                    className={`${btn.secondary} hover:shadow-lg`}
                    onClick={fetchData}
                  >
                    <Icon.Refresh className="h-4 w-4" /> Refresh
                  </button>
                </div>
              </div>
            ) : (
              <table className={`text-[14px] ${density === "compact" ? "" : ""}`} style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
                <colgroup>
                  {/* checkbox */}                        <col style={{ width: '48px', minWidth: '48px' }} />
                  {/* Order # */}                          <col style={{ width: '120px', minWidth: '110px' }} />
                  {/* AWB # */}                           <col style={{ width: '120px', minWidth: '100px' }} />
                  {/* Pickup */}                          <col style={{ width: '120px', minWidth: '100px' }} />
                  {/* Delivery */}                        <col style={{ width: '120px', minWidth: '100px' }} />
                  {/* Pay */}                             <col style={{ width: '90px', minWidth: '80px' }} />
                  {/* Package Details */}                 <col style={{ width: '140px', minWidth: '120px' }} />
                  {/* Items */}                           <col style={{ width: '90px', minWidth: '80px' }} />
                  {/* Order Status */}                    <col style={{ width: '120px', minWidth: '100px' }} />
                  {/* Confirmation Status */}             <col style={{ width: '140px', minWidth: '120px' }} />
                  {/* Courier */}                         <col style={{ width: '150px', minWidth: '130px' }} />
                  {/* Actions (sticky) */}                <col style={{ width: '130px', minWidth: '120px' }} />
                </colgroup>
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50/95 supports-[backdrop-filter]:backdrop-blur border-b border-slate-200 text-slate-700 shadow-[0_1px_0_0_rgba(15,23,42,0.08)]">
                    <th className="p-2 w-12 align-middle">
                      <div className="flex items-center justify-center">
                        <input ref={headerCheckboxRef} type="checkbox" checked={allOnPageSelected} onChange={(e) => toggleSelectAllOnPage(e.target.checked)} className="h-4 w-4 accent-indigo-600 cursor-pointer" title={allOnPageSelected ? "Unselect all on this page" : "Select all on this page"} />
                      </div>
                    </th>
                    {[
                      "Order #",
                      "AWB #",
                      "Pickup",
                      "Delivery",
                      "Pay",
                      "Package Details",
                      "Items",
                      "Order Status",
                      "Confirmation",
                      "Courier",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        className={`p-2 text-center align-middle text-[13px] font-semibold ${h === "Actions" ? "sticky right-0 z-20 bg-slate-50/95" : ""
                          }`}
                      >
                        {h}
                      </th>
                    ))}

                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {shipments.map((o, i) => {
                    const orderedPkgs = getOrderedPkgs(o)
                    const fulfilledPkgs = getFulfilledPkgs(o)
                    const orderedItems = flattenItems(orderedPkgs)
                    const fulfilledItems = flattenItems(fulfilledPkgs)
                    const orderedQty = sumQty(orderedItems)
                    const fulfilledQty = sumQty(fulfilledItems)
                    const pendingQty = Math.max(orderedQty - fulfilledQty, 0)
                    const orderedValue = sumValue(orderedItems)
                    const fulfilledValue = sumValue(fulfilledItems)
                    const invoiceValue = getInvoiceValue(o)
                    const deadOrdered = deadKgOf(orderedPkgs)
                    const deadFulfilled = deadKgOf(fulfilledPkgs)
                    const volOrdered = volKgOf(orderedPkgs)
                    const volFulfilled = volKgOf(fulfilledPkgs)
                    const hasAnyFulfilled = fulfilledQty > 0
                    const isCancelling = cancellingIds.includes(o.id || o.order_number)
                    const orderStatus = String(o?.order_status || "").toUpperCase()
                    const isPendingStatus = orderStatus === "PENDING"
                    const isConfirmedStatus = orderStatus === "CONFIRMED"
                    const isActionableStatus = isPendingStatus || isConfirmedStatus
                    const canCreatePending = isActionableStatus && orderedQty > fulfilledQty
                    const canCancelPending = isActionableStatus && !hasAnyFulfilled
                    const canEditPending = isActionableStatus
                    const mpChannelOrderNumber = o?.marketplace_channel_order_number || null

                    const awbs = Array.from(new Set((fulfilledPkgs || []).map((p) => p?.waybill).filter(Boolean)))

                    // Get chip style for order_status
                    const getOrderStatusChip = (status) => {
                      const statusUpper = String(status || "").toUpperCase()
                      switch (statusUpper) {
                        case "PENDING":
                          return chip.warn
                        case "CONFIRMED":
                          return chip.info
                        case "READY_FOR_PICKUP":
                          return chip.primary
                        case "FULFILLED":
                          return chip.success
                        default:
                          return chip.muted
                      }
                    }

                    const orderStatusBadge = {
                      text: (o?.order_status || "—").replace(/_/g, " "),
                      cls: getOrderStatusChip(o?.order_status)
                    }

                    const shipmentDetails = o?.shipment_details || o?.market_place_shipment_details
                    const orderType = shipmentDetails?.order_details?.order_type
                      ? String(shipmentDetails.order_details.order_type).toUpperCase()
                      : "—"

                    const key = rowKey(o)
                    const loadingCourier = loadingCarrierKeys.has(key)
                    const courierOptions = carrierOptionsByKey[key] || []
                    const selectedCarrierId = carrierSelectionByKey[key] || "RECOMMENDATION"

                    const cheapestOption =
                      courierOptions.length > 0
                        ? courierOptions.reduce(
                          (min, opt) => (opt.price < min.price ? opt : min),
                          courierOptions[0]
                        )
                        : null

                    const isUsingRecommendation = selectedCarrierId === "RECOMMENDATION"
                    const isUsingCheapest =
                      !isUsingRecommendation &&
                      cheapestOption &&
                      selectedCarrierId === cheapestOption.id
                    const isUsingNonCheapest =
                      !isUsingRecommendation &&
                      cheapestOption &&
                      selectedCarrierId !== cheapestOption.id
                    const pickupDetails = o.pickup_details || o.market_place_pickup_details || null
                    const pickupOrganisation = pickupDetails?.pickup_organisation || "—"
                    const pickupPostalCode = pickupDetails?.pickup_postal_code || ""
                    const pickupFullAddress = pickupDetails
                      ? [
                        pickupDetails.pickup_address,
                        pickupDetails.pickup_city,
                        pickupDetails.pickup_state,
                        pickupDetails.pickup_postal_code,
                        pickupDetails.pickup_country_code,
                      ]
                        .filter(Boolean)
                        .join(", ")
                      : ""

                    const dropDetails = o.drop_details || o.market_place_customer_drop_details || null
                    const dropPoc = dropDetails?.drop_poc || "—"
                    const dropPostalCode = dropDetails?.drop_postal_code || ""
                    const dropFullAddress = dropDetails
                      ? [
                        dropDetails.drop_address,
                        dropDetails.drop_city,
                        dropDetails.drop_state,
                        dropDetails.drop_postal_code,
                        dropDetails.drop_country_code,
                      ]
                        .filter(Boolean)
                        .join(", ")
                      : ""

                    return (
                      <React.Fragment key={o.id || o.order_number}>
                        <tr className={`hover:bg-slate-50/60 odd:bg-white even:bg-slate-50/40 ${densityRow} row-pad border-b border-slate-200`}>
                          <td className="p-2 w-12 align-middle">
                            <div className="flex items-center justify-center">
                              <input type="checkbox" checked={isSelectedOnPage(o)} onChange={() => toggleSelectOne(rowKey(o))} className="h-4 w-4 accent-indigo-600 cursor-pointer" title={isSelectedOnPage(o) ? "Unselect" : "Select"} />
                            </div>
                          </td>

                          {/* Order */}
                          <td className="p-2 text-left align-middle">
                            <div className="font-semibold text-slate-900">{o.order_number}</div>
                            <div className="text-xs text-slate-500 whitespace-nowrap">
                              {o.created_date_time ? new Date(o.created_date_time).toLocaleString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              }) : "—"}
                            </div>
                            <div className="mt-1 flex items-center gap-2">
                              <span className={`${chip.base} ${chip.primary}`}>{o.market_place_channel_name || "—"}</span>
                              {mpChannelOrderNumber ? (
                                <span className={`${chip.base} ${chip.muted}`} title="Marketplace Channel Order Number">{mpChannelOrderNumber}</span>
                              ) : (
                                <span className={`${chip.base} ${chip.muted}`}>—</span>
                              )}
                            </div>
                          </td>

                          {/* AWB */}
                          <td className="p-2 text-center align-middle">
                            {awbs.length ? (
                              <div className="flex flex-wrap gap-1 justify-center">
                                {awbs.map((a) => (
                                  <span key={a} className={`${chip.base} ${chip.primary}`}>{a}</span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-slate-400">—</div>
                            )}
                            {fulfilledQty > 0 && fulfilledQty < orderedQty && (
                              <div className="text-[11px] mt-1 text-slate-500">Some items unfulfilled</div>
                            )}
                          </td>

                          {/* Pickup */}
                          <td className="p-2 align-middle relative">
                            {pickupDetails ? (
                              <>
                                <div className="flex items-start gap-1">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="text-slate-900 text-sm font-medium truncate block">{pickupOrganisation}</span>
                                      <button
                                        onClick={() => setShowPickupAddress(prev => ({ ...prev, [key]: !prev[key] }))}
                                        className="text-slate-400 hover:text-indigo-600 cursor-pointer transition-colors flex-shrink-0"
                                        aria-label="Show pickup address"
                                      >
                                        <Icon.Info className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                    {pickupPostalCode && (
                                      <div className="text-slate-500 text-xs mt-0.5">
                                        {pickupPostalCode}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {showPickupAddress[key] && (
                                  <div className="absolute z-50 mt-1 p-2 bg-slate-900 text-white text-xs rounded-lg shadow-lg max-w-[250px] left-0">
                                    <div className="flex justify-between items-start gap-2">
                                      <div>{pickupFullAddress}</div>
                                      <button
                                        onClick={() => setShowPickupAddress(prev => ({ ...prev, [key]: false }))}
                                        className="text-white hover:text-slate-300 flex-shrink-0"
                                      >
                                        <Icon.X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </td>

                          {/* Delivery */}
                          <td className="p-2 align-middle relative">
                            {dropDetails ? (
                              <>
                                <div className="flex items-start gap-1">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="text-slate-900 text-sm font-medium truncate block">{dropPoc}</span>
                                      <button
                                        onClick={() => setShowDropAddress(prev => ({ ...prev, [key]: !prev[key] }))}
                                        className="text-slate-400 hover:text-indigo-600 cursor-pointer transition-colors flex-shrink-0"
                                        aria-label="Show delivery address"
                                      >
                                        <Icon.Info className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                    {dropPostalCode && (
                                      <div className="text-slate-500 text-xs mt-0.5">
                                        {dropPostalCode}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {showDropAddress[key] && (
                                  <div className="absolute z-50 mt-1 p-2 bg-slate-900 text-white text-xs rounded-lg shadow-lg max-w-[250px] left-0">
                                    <div className="flex justify-between items-start gap-2">
                                      <div>{dropFullAddress}</div>
                                      <button
                                        onClick={() => setShowDropAddress(prev => ({ ...prev, [key]: false }))}
                                        className="text-white hover:text-slate-300 flex-shrink-0"
                                      >
                                        <Icon.X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </td>

                          {/* Pay */}
                          <td className="p-2 align-middle">
                            <div className="font-semibold text-slate-900">
                              {formatINR(invoiceValue > 0 ? invoiceValue : orderedValue)}
                            </div>

                            {fulfilledQty > 0 && (
                              <div className="text-xs text-slate-500">
                                Fulfilled: {formatINR(fulfilledValue)}
                              </div>
                            )}

                            <div className="text-xs text-slate-500 mt-1">
                              <span className="font-medium">{orderType}</span>
                            </div>
                          </td>

                          {/* Package details */}
                          <td className="p-2 whitespace-pre-line text-xs align-middle text-slate-700">
                            {`Dead: ${deadOrdered.toFixed(2)} kg • ${deadFulfilled.toFixed(2)} kg fulfilled
Vol:  ${volOrdered.toFixed(2)} kg • ${volFulfilled.toFixed(2)} kg fulfilled`}
                          </td>

                          {/* Items */}
                          <td className="p-2 align-middle text-center">
                            <button
                              onClick={() => toggleRow(i)}
                              className="text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                            >
                              {expanded[i] ? "Hide" : "View"}
                            </button>
                            <div className="text-[11px] text-slate-500 mt-1">
                              {fulfilledQty}/{orderedQty}
                              {pendingQty > 0 && <div className="text-amber-600">{pendingQty} pend</div>}
                            </div>
                          </td>

                          {/* Order Status */}
                          <td className="p-2 align-middle text-center">
                            <span className={`${chip.base} ${orderStatusBadge.cls} break-words whitespace-normal text-[11px]`}>
                              {orderStatusBadge.text}
                            </span>
                          </td>

                          {/* Confirmation Status with Updated Date Time */}
                          <td className="p-2 align-middle">
                            <div className="flex flex-col gap-1.5 items-center">
                              {/* Confirmation Status */}
                              {o?.confirmation_details?.confirmation_status ? (
                                <span className={`${chip.base} ${
                                  String(o.confirmation_details.confirmation_status).toUpperCase() === "CONFIRMED" 
                                    ? chip.success 
                                    : String(o.confirmation_details.confirmation_status).toUpperCase() === "PENDING"
                                    ? chip.warn
                                    : String(o.confirmation_details.confirmation_status).toUpperCase() === "CANCELLED"
                                    ? chip.danger
                                    : chip.muted
                                } text-[11px]`}>
                                  {String(o.confirmation_details.confirmation_status).replace(/_/g, " ")}
                                </span>
                              ) : (
                                <span className={`${chip.base} ${chip.muted} text-[11px]`} title="Message never sent to customer for confirmation">
                                  Not Sent
                                </span>
                              )}
                              
                              {/* Updated Date Time - Light shade below */}
                              {o?.updated_date_time ? (
                                <div className="text-[10px] text-slate-400 text-center mt-0.5">
                                  {new Date(o.updated_date_time).toLocaleDateString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric'
                                  })}, {new Date(o.updated_date_time).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </div>
                              ) : (
                                <div className="text-slate-300 text-[10px] text-center">—</div>
                              )}
                            </div>
                          </td>

                          {/* Courier column */}
                          <td className="p-2 align-middle">
                            <div className="flex flex-col space-y-1 text-left">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-slate-500">Courier</span>

                                <button
                                  type="button"
                                  onClick={() => isActionableStatus && handleManualCourierForOrder(o)}
                                  disabled={
                                    loadingCourier ||
                                    !isActionableStatus ||
                                    !manualCarrierEligibilityMap.get(key)
                                  }
                                  className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={
                                    !isActionableStatus
                                      ? "Available only for PENDING or CONFIRMED orders"
                                      : !manualCarrierEligibilityMap.get(key)
                                        ? "Only PENDING or CONFIRMED orders with pending quantity can fetch prices"
                                        : "Fetch courier prices for this order"
                                  }
                                >
                                  Get Prices
                                </button>
                              </div>

                              {loadingCourier ? (
                                <div className="flex items-center gap-2 text-indigo-600 text-xs">
                                  <DotLottieReact
                                    src={lottieSrc}
                                    loop
                                    autoplay
                                    style={{ width: 24, height: 24 }}
                                  />
                                  <span>Fetching couriers…</span>
                                </div>
                              ) : courierOptions && courierOptions.length > 0 ? (
                                <div className="relative">
                                  <select
                                    className="w-full appearance-none bg-white border border-slate-200 text-xs px-2 py-1.5 pr-7 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-pointer font-medium text-slate-700"
                                    value={selectedCarrierId}
                                    onChange={(e) => {
                                      const val = e.target.value
                                      setCarrierSelectionByKey((prev) => ({
                                        ...prev,
                                        [key]:
                                          val === "RECOMMENDATION"
                                            ? undefined
                                            : val,
                                      }))
                                    }}
                                  >
                                    <option
                                      value="RECOMMENDATION"
                                      className="text-slate-500"
                                    >
                                      Use recommendation
                                    </option>
                                    {courierOptions.map((opt) => (
                                      <option key={opt.id} value={opt.id}>
                                        {`${opt.name} — ₹${opt.price.toFixed(2)}`}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-slate-400">
                                    <svg
                                      className="h-3.5 w-3.5"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0l-4.24-4.24a.75.75 0 01.02-1.06z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-[11px] text-slate-400">
                                  No courier selected
                                </span>
                              )}
                            </div>
                          </td>


                          <td className="p-2 sticky right-0 align-middle bg-white">
                            <div className="flex flex-row items-center gap-1.5 justify-center">

                              {/* Create shipment */}
                              <button
                                onClick={() => isActionableStatus && canCreatePending && createShipment(o)}
                                disabled={!isActionableStatus || !canCreatePending || creatingIds.includes(o.id)}
                                className={actionIconBtn}
                                title={
                                  !isActionableStatus
                                    ? "Available only for PENDING or CONFIRMED orders"
                                    : !canCreatePending
                                      ? "All items fulfilled"
                                      : "Create shipment"
                                }
                                aria-label="Create shipment"
                              >
                                {creatingIds.includes(o.id) ? (
                                  <span className="h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Icon.Plus className="h-4 w-4" />
                                )}
                              </button>

                              {/* Edit - World-Class Enhanced UI */}
                              <div className="relative group/edit">
                                <button
                                  onClick={() => { if (isActionableStatus && canEditPending) { openEditModal(o) } }}
                                  disabled={!isActionableStatus || !canEditPending || openingEditId === o.id}
                                  className={actionIconEdit}
                                  title={
                                    !isActionableStatus
                                      ? "Edit is only available for PENDING or CONFIRMED orders"
                                      : "Edit order details (Shortcut: E)"
                                  }
                                  aria-label="Edit order"
                                  data-order-id={o.id}
                                >
                                  {openingEditId === o.id ? (
                                    <span className="h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <>
                                      <Icon.EditPencil className="h-4 w-4 text-indigo-600 group-hover/edit:text-indigo-700 transition-colors duration-200 group-hover/edit:scale-110 group-active/edit:scale-95" />
                                      {canEditPending && (
                                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-emerald-500 rounded-full ring-2 ring-white animate-pulse" />
                                      )}
                                    </>
                                  )}
                                </button>
                                
                                {/* Rich Tooltip */}
                                {!canEditPending && (
                                  <div className="invisible group-hover/edit:visible opacity-0 group-hover/edit:opacity-100 absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 transition-all duration-200 pointer-events-none">
                                    <div className="bg-slate-900 text-white text-xs rounded-lg shadow-xl px-3 py-2 whitespace-nowrap">
                                      <div className="flex items-start gap-2">
                                        <Icon.Info className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                          <div className="font-semibold mb-0.5">Edit Unavailable</div>
                                          <div className="text-slate-300">
                                            {!isActionableStatus
                                              ? "Only PENDING or CONFIRMED orders can be edited"
                                              : "This order cannot be edited"}
                                          </div>
                                        </div>
                                      </div>
                                      {/* Tooltip arrow */}
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                        <div className="border-4 border-transparent border-t-slate-900" />
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {canEditPending && (
                                  <div className="invisible group-hover/edit:visible opacity-0 group-hover/edit:opacity-100 absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 transition-all duration-200 delay-300 pointer-events-none">
                                    <div className="bg-gradient-to-br from-indigo-900 to-violet-900 text-white text-xs rounded-lg shadow-xl px-3 py-2 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <Icon.EditPencil className="h-3.5 w-3.5 text-indigo-300" />
                                        <div>
                                          <div className="font-semibold">Edit Order</div>
                                          <div className="text-indigo-200 text-[10px] mt-0.5">
                                            Click to modify order details
                                          </div>
                                        </div>
                                      </div>
                                      {/* Tooltip arrow */}
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                        <div className="border-4 border-transparent border-t-indigo-900" />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Cancel */}
                              <button
                                onClick={() => isActionableStatus && canCancelPending && setCancelDialog({ open: true, order: o })}
                                disabled={!isActionableStatus || !canCancelPending || isCancelling}
                                className={actionIconDanger}
                                title={
                                  !isActionableStatus
                                    ? "Cancel is only available for PENDING or CONFIRMED orders"
                                    : "Cancel order"
                                }
                                aria-label="Cancel order"
                              >
                                {isCancelling ? (
                                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Icon.X className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded items */}
                        {expanded[i] && (
                          <tr>
                            <td colSpan={11} className="p-3 bg-slate-50/60">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Ordered */}
                                <div className="border border-slate-200 rounded-xl bg-white shadow-md overflow-hidden">
                                  <div className="px-3 py-2 border-b border-slate-100">
                                    <h4 className="font-medium text-slate-900">Ordered Items</h4>
                                  </div>
                                  <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                      <tr className="text-slate-700">
                                        {["SKU", "Name", "Qty", "Unit Price", "Total"].map((h2) => (
                                          <th key={h2} className="p-2 text-left text-xs align-middle">
                                            {h2}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {orderedItems.length ? (
                                        orderedItems.map((it, j) => {
                                          const unitRaw = it?.item_payment_details?.unit_price
                                          const unit =
                                            typeof unitRaw === "string"
                                              ? parseFloat(unitRaw)
                                              : Number(unitRaw || 0)
                                          const qty = Number(it?.quantity || 0)
                                          return (
                                            <tr key={j} className="align-middle">
                                              <td className="p-2">{it?.item_general_details?.sku || "—"}</td>
                                              <td className="p-2">{it?.item_general_details?.item_name || "—"}</td>
                                              <td className="p-2">{qty}</td>
                                              <td className="p-2">₹{unit.toFixed(2)}</td>
                                              <td className="p-2">₹{(unit * qty).toFixed(2)}</td>
                                            </tr>
                                          )
                                        })
                                      ) : (
                                        <tr>
                                          <td className="p-3 italic text-slate-500" colSpan={5}>
                                            No items
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                    <tfoot>
                                      <tr className="bg-slate-50 text-slate-800">
                                        <td className="p-2 text-right font-medium" colSpan={2}>
                                          Totals
                                        </td>
                                        <td className="p-2 font-medium">{orderedQty}</td>
                                        <td className="p-2"></td>
                                        <td className="p-2 font-medium">{formatINR(orderedValue)}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>

                                {/* Fulfilled */}
                                <div className="border border-slate-200 rounded-xl bg-white shadow-md overflow-hidden">
                                  <div className="px-3 py-2 border-b border-slate-100">
                                    <h4 className="font-medium text-slate-900">Fulfilled Items (with AWB)</h4>
                                  </div>
                                  <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                      <tr className="text-slate-700">
                                        {["AWB", "SKU", "Name", "Qty", "Unit Price", "Total"].map((h2) => (
                                          <th key={h2} className="p-2 text-left text-xs align-middle">
                                            {h2}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {fulfilledItems.length ? (
                                        fulfilledItems.map((it, j) => {
                                          const unitRaw = it?.item_payment_details?.unit_price
                                          const unit =
                                            typeof unitRaw === "string"
                                              ? parseFloat(unitRaw)
                                              : Number(unitRaw || 0)
                                          const qty = Number(it?.quantity || 0)
                                          return (
                                            <tr key={j} className="align-middle">
                                              <td className="p-2">
                                                {it._awb ? (
                                                  <span className={`${chip.base} ${chip.primary}`}>{it._awb}</span>
                                                ) : (
                                                  "—"
                                                )}
                                              </td>
                                              <td className="p-2">{it?.item_general_details?.sku || "—"}</td>
                                              <td className="p-2">{it?.item_general_details?.item_name || "—"}</td>
                                              <td className="p-2">{qty}</td>
                                              <td className="p-2">₹{unit.toFixed(2)}</td>
                                              <td className="p-2">₹{(unit * qty).toFixed(2)}</td>
                                            </tr>
                                          )
                                        })
                                      ) : (
                                        <tr>
                                          <td className="p-3 italic text-slate-500" colSpan={6}>
                                            Nothing fulfilled yet
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                    <tfoot>
                                      <tr className="bg-slate-50 text-slate-800">
                                        <td className="p-2 text-right font-medium" colSpan={3}>
                                          Totals
                                        </td>
                                        <td className="p-2 font-medium">{fulfilledQty}</td>
                                        <td className="p-2"></td>
                                        <td className="p-2 font-medium">{formatINR(fulfilledValue)}</td>
                                      </tr>
                                    </tfoot>
                                  </table>

                                  {pendingQty > 0 && (
                                    <div className="p-3 text-xs text-amber-700 bg-amber-50 border-t border-amber-100">
                                      Pending qty: <b>{pendingQty}</b> (ready for next shipment)
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* PAGINATION */}
        <div className="sticky bottom-0 left-0 right-0 z-29 mt-8">
          <div className="max-w-none px-2 md:px-4">
            <div className="flex items-center justify-between bg-white/85 supports-[backdrop-filter]:backdrop-blur-md border border-slate-200 rounded-xl shadow-2xl px-4 py-3">
              <div className="text-xs text-slate-600">
                Showing <b>{shipments.length}</b> of <b>{totalCount}</b>
              </div>

              {/* Prev */}
              <button
                onClick={prevPage}
                disabled={page === 1}
                aria-label="Previous page"
                className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-white ring-1 ring-slate-200 shadow-sm hover:shadow-md active:shadow disabled:opacity-50 disabled:shadow-none"
              >
                <Icon.Left className="h-4 w-4 text-slate-700" />
              </button>

              {/* Current page */}
              <div className="px-5 py-2 rounded-full bg-gradient-to-b from-white to-slate-50 ring-1 ring-slate-200 shadow-md text-slate-900 font-semibold tracking-wide">
                {page}
              </div>

              {/* Next + page size */}
              <div className="flex items-center gap-3">
                <button
                  onClick={nextPage}
                  disabled={shipments.length < limit}
                  aria-label="Next page"
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-white ring-1 ring-slate-200 shadow-sm hover:shadow-md active:shadow disabled:opacity-50 disabled:shadow-none"
                >
                  <Icon.Right className="h-4 w-4 text-slate-700" />
                </button>

                <select
                  value={limit}
                  onChange={handleLimitChange}
                  title="Rows per page"
                  className="w-[110px] px-3 py-2 rounded-full bg-white ring-1 ring-slate-200 shadow-sm focus:ring-2 focus:ring-indigo-300 outline-none text-slate-800"
                >
                  {[20, 50, 100, 500].map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* EDIT MODAL */}
        {modalVisible && editingOrder && (
          <EditOrderModal
            order={editingOrder}
            pickupList={pickupList}
            onClose={() => setModalVisible(false)}
            onSave={async (updated) => {
              try {
                const jwt = localStorage.getItem("authToken") || ""
                await axios.put(API.updateMarketplaceOrders(), updated, {
                  headers: { Authorization: jwt, "Content-Type": "application/json" },
                })
                setModalVisible(false)
                await fetchData()
              } catch (err) {
                console.error("Update failed", err)
              }
            }}
          />
        )}

        {/* CANCEL ORDER DIALOG */}
        {cancelDialog.open && (
          <CancelOrderDialog
            open={cancelDialog.open}
            onClose={() => setCancelDialog({ open: false, order: null })}
            onConfirm={(reason) => {
              const order = cancelDialog.order
              setCancelDialog({ open: false, order: null })
              cancelOrder(order, reason)
            }}
          />
        )}

        {/* INFO MODAL */}
        {modal.open && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 text-center">
              <h3 className="text-lg font-semibold mb-2 text-slate-900">{modal.title}</h3>
              <p className="text-slate-700 whitespace-pre-line">{modal.message}</p>
              <div className="mt-5">
                <button onClick={closeModal} className={btn.primary} autoFocus>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* APPLY PICKUP OVERLAY */}
        {applyingPickup && (
          <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center">
              <div className="flex justify-center mb-3">
                <DotLottieReact src={lottieSrc} loop autoplay style={{ width: 80, height: 80 }} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Applying pickup…</h3>
              <p className="text-slate-700 text-sm">
                {applyProgress.done}/{applyProgress.total} updated
              </p>
              <div className="mt-3 h-2 w-full bg-slate-100 rounded-full overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-indigo-600 to-violet-600 transition-all"
                  style={{
                    width:
                      applyProgress.total > 0
                        ? `${(applyProgress.done / applyProgress.total) * 100}%`
                        : "0%",
                  }}
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-3">Please don't close this tab.</p>
            </div>
          </div>
        )}

        {/* BULK UPLOAD MODAL */}
        <BulkUploadModal
          open={bulkUploadModalOpen}
          onClose={() => setBulkUploadModalOpen(false)}
          onSuccess={() => {
            fetchData()
          }}
        />

        {/* CSV Column Selection Modal */}
        {showCSVColumnModal && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowCSVColumnModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Icon.Download className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Export CSV - Select Columns</h3>
                    <p className="text-emerald-100 text-sm mt-0.5">{selectedRows.size} {selectedRows.size === 1 ? 'order' : 'orders'} selected</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCSVColumnModal(false)}
                  className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                >
                  <Icon.X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto flex-1">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-slate-600">Select which columns to include in your CSV export</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const allKeys = allCSVColumns.map(col => col.key)
                          setSelectedCSVColumns(new Set(allKeys))
                        }}
                        className="text-xs px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => {
                          const defaultKeys = allCSVColumns.filter(col => col.defaultSelected).map(col => col.key)
                          setSelectedCSVColumns(new Set(defaultKeys))
                        }}
                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-50 text-slate-700 hover:bg-slate-100 font-medium transition-colors"
                      >
                        Reset to Default
                      </button>
                    </div>
                  </div>
                <div className="text-xs text-slate-500 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                  <span className="font-semibold text-emerald-700">Default:</span> Only "channel_order_id" is selected by default
                </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                  {allCSVColumns.map((column) => {
                    const isSelected = selectedCSVColumns.has(column.key)
                    return (
                      <label
                        key={column.key}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50 hover:bg-emerald-100'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const newSet = new Set(selectedCSVColumns)
                            if (e.target.checked) {
                              newSet.add(column.key)
                            } else {
                              newSet.delete(column.key)
                            }
                            setSelectedCSVColumns(newSet)
                          }}
                          className="h-5 w-5 accent-emerald-600 cursor-pointer flex-shrink-0"
                        />
                        <span className={`text-sm font-medium flex-1 ${isSelected ? 'text-emerald-900' : 'text-slate-700'}`}>
                          {column.label}
                        </span>
                        {column.defaultSelected && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800 font-semibold">
                            Default
                          </span>
                        )}
                      </label>
                    )
                  })}
                </div>

                {selectedCSVColumns.size === 0 && (
                  <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <p className="text-sm text-rose-700 font-medium">⚠️ Please select at least one column to export</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-emerald-700">{selectedCSVColumns.size}</span> of {allCSVColumns.length} columns selected
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCSVColumnModal(false)}
                    className="px-4 py-2 text-sm font-medium rounded-xl bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDownloadCSV()}
                    disabled={selectedCSVColumns.size === 0 || downloadingCSV}
                    className="px-5 py-2 text-sm font-medium rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all"
                  >
                    {downloadingCSV ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Icon.Download className="h-4 w-4 inline mr-2" />
                        Export CSV
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`
  /* Compact date input shell to match 42px controls */
  .date-compact > .MuiPaper-root,
  .date-compact > .MuiPaper-root.MuiPaper-elevation,
  .date-compact > .MuiPaper-root.MuiPaper-elevation1 {
    height: 42px !important;
    padding: 0 0.75rem !important;
    border-radius: 0.75rem !important;
    box-shadow: none !important;
    display: flex; align-items: center; justify-content: space-between;
  }
  .date-compact .MuiTypography-root { font-size: 13px; font-weight: 500; }
  .date-compact .MuiSvgIcon-root  { font-size: 18px; }

  /* density: compact (global) */
  .density-compact table { font-size: 12px; }
  .density-compact th,
  .density-compact td { padding: 0.5rem !important; }
  .density-compact thead th { padding-top: 0.4rem !important; padding-bottom: 0.4rem !important; }
  .density-compact .row-pad { padding-top: 0.25rem !important; padding-bottom: 0.25rem !important; }

  /* Advanced Animations */
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes pulse-ring {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
    }
  }

  .animate-blob {
    animation: blob 7s infinite;
  }
  
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  
  .animate-shimmer {
    animation: shimmer 2s infinite linear;
  }
  
  .animate-fadeInUp {
    animation: fadeInUp 0.6s ease-out;
  }
  
  /* Enhanced table row hover */
  tbody tr {
    transition: all 0.2s ease;
  }
  
  tbody tr:hover {
    transform: scale(1.001);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    position: relative;
    z-index: 1;
  }
  
  /* Smooth expand/collapse */
  .expand-content {
    animation: fadeInUp 0.3s ease-out;
  }
  
  /* Enhanced button interactions */
  button:active {
    transform: scale(0.98);
  }
  
  /* Focus rings for accessibility */
  *:focus-visible {
    outline: 2px solid rgba(99, 102, 241, 0.5);
    outline-offset: 2px;
  }
  
  /* Backdrop blur support */
  @supports (backdrop-filter: blur(10px)) {
    .supports-backdrop-blur {
      backdrop-filter: blur(10px);
    }
  }
`}</style>

    </div>
  )
}
